/**
 * useWorkspaceTrigger — обработчики для аватарки профиля:
 *   • правый клик (desktop) → открыть WorkspaceSwitcher
 *   • long-press 450мс без существенного движения (mobile) → то же
 *
 * Не перебивает обычный клик/тап (тот по-прежнему открывает профиль).
 * Возвращает спред props для button/div.
 */
import { useRef, type PointerEvent as RPointerEvent, type MouseEvent as RMouseEvent } from "react";


const LONG_PRESS_MS = 450;
const MOVE_TOLERANCE_PX = 8;


export function useWorkspaceTrigger() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  /** Если long-press сработал — мы блокируем последующий click чтобы
   *  не открылся профиль одновременно со switcher'ом. */
  const suppressClickRef = useRef(false);

  function trigger() {
    document.dispatchEvent(new CustomEvent("kb-open-workspace-switcher"));
  }

  function clearTimer() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }

  function onContextMenu(e: RMouseEvent<HTMLElement>) {
    e.preventDefault();
    trigger();
  }

  function onPointerDown(e: RPointerEvent<HTMLElement>) {
    // Игнорируем правый клик (его обрабатывает onContextMenu)
    if (e.button === 2) return;
    startedRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    suppressClickRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(() => {
      // Если ещё держат и не сдвинулись — это long-press
      if (startedRef.current) {
        suppressClickRef.current = true;
        trigger();
      }
      timerRef.current = null;
    }, LONG_PRESS_MS);
  }

  function onPointerMove(e: RPointerEvent<HTMLElement>) {
    if (!startedRef.current) return;
    const dx = e.clientX - startedRef.current.x;
    const dy = e.clientY - startedRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > MOVE_TOLERANCE_PX) {
      clearTimer();
      startedRef.current = null;
    }
  }

  function onPointerUp() {
    clearTimer();
    startedRef.current = null;
  }

  function onPointerCancel() {
    clearTimer();
    startedRef.current = null;
  }

  /** Обёртка над оригинальным onClick — пропускает тап, но не даёт
   *  сработать onClick (и default-навигации Link) если только что
   *  отработал long-press. */
  function wrapClick(handler: (e: RMouseEvent<HTMLElement>) => void) {
    return (e: RMouseEvent<HTMLElement>) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      handler(e);
    };
  }

  return {
    /** Спред эти props на element (button/div/Link). */
    handlers: {
      onContextMenu,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    } as const,
    wrapClick,
  };
}
